<script setup lang="ts">
import { motion } from 'motion-v'

const { t, tm, rt } = useI18n()

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${import.meta.env.VITE_MONOLITH_URL}/free-demo`,
    },
  ],
})

useSeoMeta({
  title: t('contact_us/free_demo.meta.title'),
  description: t('contact_us/free_demo.meta.description'),

  ogTitle: t('contact_us/free_demo.meta.title'),
  ogDescription: t('contact_us/free_demo.meta.description'),
  ogImage:
        `${S3_ASSETS_URL}/meta-default.jpg`,
})

const captchaService = useScript(
  {
    src: 'https://www.google.com/recaptcha/api.js?render=explicit',
    async: true,
    defer: true,
    crossorigin: false,
  },
  {
    use: () => window.grecaptcha,
  },
)

const captchaElement = useTemplateRef('captcha')

const [isDisclaimerExpanded, toggleDisclaimer] = useToggle(false)

const isFormSubmitted = ref(false)

const isScreenLG = useMediaQuery('(min-width: 1024px)')

const form = useForm({
  defaultValues: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    spot_name: '',
    restaurant_metro_area: '',
    time_window: 'anytime',
    referral: '',
    referral_details: '',
    postal_code: '',
    message: '',
    disclaimer: false,
    captcha: '',
  },
  validators: {
    onSubmit: freeDemoValidationSchema,
  },
  onSubmit: async ({ value }) => {
    isFormSubmitted.value = false

    try {
      const data = new FormData()

      data.append('purpose', 'free-demo')

      Object.entries(value).forEach(([key, value]) => {
        switch (key) {
          case 'referral':
            return data.append('how_did_you_hear', String(value))

          case 'referral_details':
            return data.append(
              'how_did_you_hear_details',
              String(value),
            )

          case 'postal_code':
            return data.append('zipcode', String(value))

          default:
            data.append(key, String(value))
        }
      })

      const response = await useRequest('/contact-us').post(data).text()

      if (response.statusCode.value !== 200) {
        return Promise.reject({
          message: 'Something went wrong while submitting your request.',
        })
      }

      window.gtag('event', 'lead_free_demo')

      form.reset()
      captchaService.proxy.reset()
      isFormSubmitted.value = true
    }
    catch (error) {
      captchaService.proxy.reset()
      console.error(error)
    }
  },
})

captchaService.proxy.ready(() => {
  if (!captchaElement.value) return

  captchaService.proxy.render(captchaElement.value, {
    sitekey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
    size: 'compact',
    callback: (value) => {
      form.setFieldValue('captcha', value)
    },
  })

  // Handle edge-case for screen-reader
  const field = captchaElement.value.querySelector('textarea')
  field?.setAttribute('aria-hidden', 'true')
  field?.setAttribute('aria-readonly', 'true')
  field?.setAttribute('aria-label', 'reCaptcha Validation')
})
</script>

<template>
  <div class="isolate flex min-h-dvh flex-col bg-[#F8F8F0]">
    <LayoutNavbarAlt />

    <section class="mx-auto grid max-w-screen-xl place-items-start gap-8 px-2 pb-8 lg:grid-cols-2 lg:px-8 lg:py-16">
      <ContactPartialDemoContext v-if="isScreenLG" />

      <div class="flex w-full flex-col rounded-2xl bg-gray-600/5">
        <LayoutContactNavbar class="lg:px-10" />

        <ContactPartialDemoContext
          v-if="!isScreenLG"
          class="!gap-y-4 p-5 text-center"
        />

        <motion.div class="overflow-clip rounded-2xl border border-gray-200 bg-white p-4 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              v-if="isFormSubmitted"
              class="grid gap-y-5 text-balance text-center"
              :initial="{ height: 0, opacity: 0 }"
              :animate="{ height: 'auto', opacity: 1 }"
              :exit="{ height: 0, opacity: 0 }"
            >
              <img
                :src="`${S3_ASSETS_URL}/contact/success-illustration.svg`"
                class="mx-auto max-w-32"
              >

              <strong class="text-4xl font-medium">
                {{
                  t("contact_us/free_demo.form.success.title")
                }}
              </strong>

              <p class="text-lg">
                <span
                  v-for="(message, messageIndex) in tm(
                    'contact_us/free_demo.form.success.messages',
                  )"
                  :key="messageIndex"
                  class="block"
                >
                  {{ rt(message) }}
                </span>
              </p>
            </motion.div>

            <motion.div
              v-else
              :initial="{ height: 0, opacity: 0 }"
              :animate="{ height: 'auto', opacity: 1 }"
              :exit="{ height: 0, opacity: 0 }"
            >
              <i18n-t
                keypath="contact_us/free_demo.form.title"
                class="text-balance text-center text-lg font-semibold lg:text-left"
                tag="h4"
              >
                <template #title_placeholder>
                  <span class="text-base text-gray-500 lg:text-lg">
                    {{
                      t(
                        `contact_us/free_demo.form.title_placeholder`,
                      )
                    }}
                  </span>
                </template>
              </i18n-t>

              <form
                class="mt-5 grid grid-cols-2 gap-5"
                @submit.prevent="form.handleSubmit"
              >
                <form.Field
                  name="first_name"
                  :validators="{
                    onChange: nameSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiField
                      :id="field.name"
                      type="text"
                      :label="t(
                        'contact_us/free_demo.form.fields.first_name',
                      )
                      "
                      placeholder="James"
                      autocomplete="given-name"
                      container-class="col-span-2 lg:col-span-1 w-full"
                      required
                      :value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      @blur="field.handleBlur"
                      @input="
                        (e) =>
                          field.handleChange(
                            e.target.value,
                          )
                      "
                    />
                  </template>
                </form.Field>

                <form.Field
                  name="last_name"
                  :validators="{
                    onChange: nameSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiField
                      :id="field.name"
                      type="text"
                      :label="t(
                        'contact_us/free_demo.form.fields.last_name',
                      )
                      "
                      placeholder="Smith"
                      autocomplete="family-name"
                      container-class="col-span-2 lg:col-span-1 w-full"
                      required
                      :value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      @blur="field.handleBlur"
                      @input="
                        (e) =>
                          field.handleChange(
                            e.target.value,
                          )
                      "
                    />
                  </template>
                </form.Field>

                <form.Field
                  name="email"
                  :validators="{
                    onChange: emailSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiField
                      :id="field.name"
                      type="email"
                      :label="t(
                        'contact_us/free_demo.form.fields.email',
                      )
                      "
                      placeholder="james@acme.com"
                      autocomplete="email"
                      container-class="col-span-2 lg:col-span-1 w-full"
                      required
                      :value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      @blur="field.handleBlur"
                      @input="
                        (e) =>
                          field.handleChange(
                            e.target.value,
                          )
                      "
                    />
                  </template>
                </form.Field>

                <form.Field
                  name="phone"
                  :validators="{
                    onChange: phoneSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiField
                      :id="field.name"
                      type="tel"
                      :label="t(
                        'contact_us/free_demo.form.fields.phone',
                      )
                      "
                      placeholder="+123456789"
                      autocomplete="tel"
                      container-class="col-span-2 lg:col-span-1 w-full"
                      required
                      :value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      @blur="field.handleBlur"
                      @input="
                        (e) =>
                          field.handleChange(
                            e.target.value,
                          )
                      "
                    />
                  </template>
                </form.Field>

                <form.Field
                  name="spot_name"
                  :validators="{
                    onChange: nameSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiField
                      :id="field.name"
                      type="text"
                      :label="t(
                        'contact_us/free_demo.form.fields.spot_name',
                      )
                      "
                      placeholder="Acme Foods"
                      autocomplete="off"
                      container-class="col-span-2 lg:col-span-1 w-full"
                      required
                      :value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      @blur="field.handleBlur"
                      @input="
                        (e) =>
                          field.handleChange(
                            e.target.value,
                          )
                      "
                    />
                  </template>
                </form.Field>

                <form.Field
                  name="restaurant_metro_area"
                  :validators="{
                    onChange: nameSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiField
                      :id="field.name"
                      type="text"
                      :label="t(
                        'contact_us/free_demo.form.fields.restaurant_metro_area',
                      )
                      "
                      placeholder="Springfield"
                      autocomplete="off"
                      container-class="col-span-2 lg:col-span-1 w-full"
                      required
                      :value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      @blur="field.handleBlur"
                      @input="
                        (e) =>
                          field.handleChange(
                            e.target.value,
                          )
                      "
                    />
                  </template>
                </form.Field>

                <form.Field
                  name="time_window"
                  :validators="{
                    onChange: timeWindowSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiSelect
                      :id="field.name"
                      :label="t(
                        'contact_us/free_demo.form.fields.time_window',
                      )
                      "
                      placeholder="Please select"
                      autocomplete="off"
                      container-class="col-span-2 lg:col-span-1 w-full"
                      :items="contactTimeFrames"
                      :model-value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      @blur="field.handleBlur"
                      @update:model-value="
                        (value) =>
                          field.handleChange(value)
                      "
                    />
                  </template>
                </form.Field>

                <form.Field
                  name="referral"
                  :validators="{
                    onChange: referralSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiSelect
                      :id="field.name"
                      :label="t(
                        'contact_us/free_demo.form.fields.referral',
                      )
                      "
                      placeholder="Please select"
                      autocomplete="off"
                      container-class="col-span-2 lg:col-span-1 w-full"
                      :items="contactReferrers"
                      :model-value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      @blur="field.handleBlur"
                      @update:model-value="
                        (value) =>
                          field.handleChange(value)
                      "
                    />
                  </template>
                </form.Field>

                <form.Subscribe>
                  <template #default="ctx">
                    <form.Field
                      v-if="
                        [
                          'another_restaurant',
                          'other',
                        ].includes(ctx.values.referral)
                      "
                      name="referral_details"
                      :validators="{
                        onChange: referralDetailsSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="text"
                          :label="t(
                            'contact_us/free_demo.form.fields.referral_details',
                          )
                          "
                          placeholder="Tell us the details..."
                          autocomplete="off"
                          container-class="col-span-2 w-full"
                          :value="field.state.value"
                          :error="field.state.meta
                            .errors[0]?.message
                          "
                          @blur="field.handleBlur"
                          @input="
                            (e) =>
                              field.handleChange(
                                e.target.value,
                              )
                          "
                        />
                      </template>
                    </form.Field>
                  </template>
                </form.Subscribe>

                <form.Subscribe>
                  <template #default="ctx">
                    <form.Field
                      v-if="
                        ctx.values.referral === 'nysra'
                      "
                      name="postal_code"
                      :validators="{
                        onChange: postalCodeSchema,
                      }"
                    >
                      <template #default="{ field }">
                        <UiField
                          :id="field.name"
                          type="text"
                          :label="t(
                            'contact_us/free_demo.form.fields.postal_code',
                          )
                          "
                          placeholder="#####"
                          autocomplete="postal-code"
                          container-class="col-span-2 w-full"
                          :value="field.state.value"
                          :error="field.state.meta
                            .errors[0]?.message
                          "
                          @blur="field.handleBlur"
                          @input="
                            (e) =>
                              field.handleChange(
                                e.target.value,
                              )
                          "
                        />
                      </template>
                    </form.Field>
                  </template>
                </form.Subscribe>

                <form.Field
                  name="message"
                  :validators="{
                    onChange: messageSchema,
                  }"
                >
                  <template #default="{ field }">
                    <UiField
                      :id="field.name"
                      type="text"
                      :label="t(
                        'contact_us/free_demo.form.fields.message',
                      )
                      "
                      placeholder="Type your message here..."
                      autocomplete="off"
                      container-class="col-span-2 w-full"
                      class="min-h-24"
                      :value="field.state.value"
                      :error="field.state.meta.errors[0]
                        ?.message
                      "
                      as-child
                      @blur="field.handleBlur"
                      @input="
                        (e) =>
                          field.handleChange(
                            e.target.value,
                          )
                      "
                    >
                      <textarea />
                    </UiField>
                  </template>
                </form.Field>

                <div class="col-span-2 grid auto-cols-auto gap-x-2 gap-y-4 lg:grid-flow-col">
                  <form.Field
                    name="disclaimer"
                    :validators="{
                      onChange: disclaimerSchema,
                    }"
                  >
                    <template #default="{ field }">
                      <motion.div>
                        <UiCheckbox
                          :id="field.name"
                          :error="field.state.meta
                            .errors[0]?.message
                          "
                          :model-value="field.state.value
                          "
                          :container-class="[
                            'col-span-2 w-full overflow-clip text-balance',
                            {
                              '!items-start':
                                isDisclaimerExpanded,
                            },
                          ]"
                          @update:model-value="
                            (value) => {
                              if (
                                typeof value !==
                                'boolean'
                              )
                                return;
                              field.handleChange(
                                value,
                              );
                            }
                          "
                        >
                          <span class="block">
                            {{
                              t(
                                "contact_us/free_demo.form.fields.disclaimer.base",
                              )
                            }}

                            <motion.button
                              v-if="
                                !isDisclaimerExpanded
                              "
                              type="button"
                              class="shrink-0 font-bold text-orange-500 underline"
                              layout-id="disclaimer-action"
                              @click.prevent="
                                toggleDisclaimer()
                              "
                            >
                              {{
                                t(
                                  "contact_us/free_demo.form.fields.disclaimer.action.expand",
                                )
                              }}
                            </motion.button>
                          </span>

                          <AnimatePresence>
                            <motion.span
                              v-if="
                                isDisclaimerExpanded
                              "
                              class="mt-5 block space-y-5"
                              :initial="{
                                height: 0,
                                opacity: 0,
                              }"
                              :animate="{
                                height: 'auto',
                                opacity: 1,
                              }"
                              :exit="{
                                height: 0,
                                opacity: 0,
                              }"
                            >
                              <span
                                v-for="(
                                  item,
                                  itemIndex
                                ) in tm(
                                  'contact_us/free_demo.form.fields.disclaimer.extended',
                                )"
                                :key="itemIndex"
                                class="inline-block"
                              >
                                {{ rt(item) }}

                                <motion.button
                                  v-if="
                                    String(
                                      Object.keys(
                                        tm(
                                          'contact_us/free_demo.form.fields.disclaimer.extended',
                                        ),
                                      )
                                        .length -
                                        1,
                                    ) ===
                                      itemIndex &&
                                      isDisclaimerExpanded
                                  "
                                  type="button"
                                  class="font-bold text-orange-500 underline"
                                  layout-id="disclaimer-action"
                                  @click.prevent="
                                    toggleDisclaimer()
                                  "
                                >
                                  {{
                                    t(
                                      "contact_us/free_demo.form.fields.disclaimer.action.collapse",
                                    )
                                  }}
                                </motion.button>
                              </span>
                            </motion.span>
                          </AnimatePresence>
                        </UiCheckbox>
                      </motion.div>
                    </template>
                  </form.Field>

                  <form.Field
                    name="captcha"
                    :validators="{
                      onChange: captchaSchema,
                    }"
                  >
                    <template #default="{ field }">
                      <div>
                        <div
                          ref="captcha"
                          class="flex items-center justify-center"
                        />

                        <AnimatePresence>
                          <motion.span
                            v-if="
                              field.state.meta
                                .errors.length
                            "
                            :initial="{
                              opacity: 0,
                              scale: 0.8,
                            }"
                            :animate="{
                              opacity: 1,
                              scale: 1,
                            }"
                            :exit="{
                              opacity: 0,
                              scale: 0.8,
                            }"
                            :transition="{
                              type: 'spring',
                              stiffness: 400,
                              damping: 25,
                              duration: 0.4,
                            }"
                            class="ml-1 shrink-0 origin-right text-xs text-red-500 dark:text-red-600"
                            role="alert"
                          >
                            {{
                              field.state.meta
                                .errors[0]
                                ?.message
                            }}
                          </motion.span>
                        </AnimatePresence>
                      </div>
                    </template>
                  </form.Field>
                </div>

                <form.Subscribe>
                  <template #default="ctx">
                    <UiButton
                      type="submit"
                      class="group col-span-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:grayscale"
                      :disabled="!ctx.canSubmit"
                    >
                      <motion.span
                        v-show="ctx.isSubmitting"
                        class="inline-flex origin-right text-xl text-white"
                        :initial="{
                          opacity: 0,
                          scale: 0,
                        }"
                        :animate="{
                          opacity: 1,
                          scale: 1,
                        }"
                        :exit="{ opacity: 0, scale: 0 }"
                        :transition="{
                          type: 'spring',
                          stiffness: 200,
                          damping: 20,
                        }"
                      >
                        <UiIcon
                          name="loader"
                          class="animate-spin"
                        />
                      </motion.span>

                      {{ t("contact_us/free_demo.form.fields.action") }}

                      <motion.span
                        v-show="!ctx.isSubmitting"
                        class="inline-flex origin-left text-xl text-white"
                        :initial="{
                          opacity: 0,
                          scale: 0,
                        }"
                        :animate="{
                          opacity: 1,
                          scale: 1,
                        }"
                        :exit="{ opacity: 0, scale: 0 }"
                        :transition="{
                          type: 'spring',
                          stiffness: 200,
                          damping: 20,
                        }"
                      >
                        <UiIcon
                          name="arrow-forward-rounded"
                          class="transition-transform duration-200 group-hover:translate-x-1"
                        />
                      </motion.span>
                    </UiButton>
                  </template>
                </form.Subscribe>
              </form>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <div class="px-5 py-3">
          <p class="text-xs">
            <i18n-t keypath="contact_us/free_demo.legal.notice">
              <template #privacy_placeholder>
                <a
                  href="/privacy"
                  class="font-medium text-orange-500 underline underline-offset-1 transition-colors hover:text-orange-600"
                >
                  {{
                    t(
                      "contact_us/free_demo.legal.privacy_placeholder",
                    )
                  }}
                </a>
              </template>

              <template #terms_placeholder>
                <a
                  href="/terms_and_conditions"
                  class="font-medium text-orange-500 underline underline-offset-1 transition-colors hover:text-orange-600"
                >
                  {{
                    t(
                      "contact_us/free_demo.legal.terms_placeholder",
                    )
                  }}
                </a>
              </template>
            </i18n-t>
          </p>
        </div>
      </div>
    </section>
  </div>
</template>
